import React from 'react';
import { format } from 'date-fns';
import { Icon } from '../Component';

/**
 * Modern Message Bubble Component
 * Renders individual chat messages with WhatsApp-like styling
 *
 * @param {Object} props
 * @param {Object} props.message - The message object
 * @param {boolean} props.isFromMe - Whether the message is from the current user
 * @param {boolean} props.showTimestamp - Whether to show the timestamp
 * @param {boolean} props.isLastInGroup - Whether this is the last message in a group
 */
const MessageBubble = React.memo(({ message, isFromMe, showTimestamp = true, isLastInGroup = false }) => {
    const getMessageStatus = () => {
        if (!isFromMe) return null;

        // For now, we'll show delivered status for all sent messages
        // This can be enhanced based on actual message status from API
        return (
            <div className="message-status">
                <Icon
                    name="check-double"
                    className="text-primary"
                    style={{ fontSize: '12px' }}
                    title="Delivered"
                />
            </div>
        );
    };

    const formatMessageTime = (timestamp) => {
        if (!timestamp) return 'Unknown time';

        try {
            const date = new Date(timestamp * 1000);
            return format(date, 'HH:mm');
        } catch (error) {
            return 'Invalid time';
        }
    };

    const formatMessageDate = (timestamp) => {
        if (!timestamp) return 'Unknown date';

        try {
            const date = new Date(timestamp * 1000);
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            if (date.toDateString() === today.toDateString()) {
                return 'Today';
            } else if (date.toDateString() === yesterday.toDateString()) {
                return 'Yesterday';
            } else {
                return format(date, 'dd/MM/yyyy');
            }
        } catch (error) {
            return 'Invalid date';
        }
    };

    return (
        <div className="message-row">
            <div
                className={`message-bubble position-relative ${isFromMe
                    ? 'bg-primary text-white ms-auto'
                    : 'bg-white text-dark border'
                    }`}
                style={{
                    maxWidth: '75%',
                    minWidth: '100px',
                    borderRadius: isFromMe
                        ? '18px 18px 4px 18px'
                        : '18px 18px 18px 4px',
                    wordBreak: 'break-word'
                }}
            >
                <div className="p-3">
                    {/* Message Content */}
                    <div className="message-content">
                        <p className="mb-0">
                            {message.body || 'No content'}
                        </p>
                    </div>

                    {/* Message Footer */}
                    <div className="message-footer d-flex justify-content-between align-items-end">
                        {showTimestamp && (
                            <div className={`message-time ${isFromMe ? 'text-white-50' : 'text-muted'}`}>
                                <small>
                                    <Icon name="clock" className="me-1" />
                                    {formatMessageTime(message.timestamp)}
                                </small>
                            </div>
                        )}

                        {/* Message Status for sent messages */}
                        {isFromMe && (
                            <div className="message-status ms-2">
                                {getMessageStatus()}
                            </div>
                        )}
                    </div>
                </div>

                {/* Message Tail */}
                <div
                    className={`message-tail position-absolute ${isFromMe ? 'end-0' : 'start-0'
                        }`}
                    style={{
                        bottom: '0',
                        width: '0',
                        height: '0',
                        borderStyle: 'solid',
                        ...(isFromMe ? {
                            right: '-8px',
                            borderWidth: '0 0 12px 12px',
                            borderColor: 'transparent transparent #007bff transparent'
                        } : {
                            left: '-8px',
                            borderWidth: '0 12px 12px 0',
                            borderColor: 'transparent #ffffff transparent transparent'
                        })
                    }}
                />
            </div>
        </div>
    );
});

MessageBubble.displayName = 'MessageBubble';

export default MessageBubble;